import axios, { AxiosError, AxiosInstance } from "axios";
import type {
  ChatMessage,
  ApiConfig,
  ChatCompletionResponse,
  StreamChunk,
  ApiError,
} from "@/types";

// Constants
const DEFAULT_CONFIG: ApiConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL || "https://api.deepseek.com/v1",
  apiKey: import.meta.env.VITE_DEEPSEEK_API_KEY || "",
  timeout: 60000,
  retryCount: 2,
  retryDelay: 1000,
};

const DEFAULT_SYSTEM_MESSAGE = `你是一个有帮助的AI助手。请用简洁、专业的方式回答问题。回答要详细易懂。`;
const DEFAULT_MODEL = 'deepseek-chat'

// API Types
interface DeepseekMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

interface DeepseekRequest {
  model: string;
  messages: DeepseekMessage[];
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
}

// API Client Class
class ApiClient {
  private client: AxiosInstance;
  private config: ApiConfig;

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.client = this.createClient();
  }

  private createClient(): AxiosInstance {
    const client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        Authorization: `Bearer ${this.config.apiKey}`,
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors(client);
    return client;
  }

  private setupInterceptors(client: AxiosInstance): void {
    // Request Interceptor
    client.interceptors.request.use(
      (config) => {
        console.log("Request:", {
          url: config.url,
          method: config.method,
          baseURL: config.baseURL,
        });
        return config;
      },
      (error) => {
        console.error("Request Error:", error);
        return Promise.reject(error);
      }
    );

    // Response Interceptor
    client.interceptors.response.use(
      (response) => {
        console.log("Response:", {
          status: response.status,
          hasData: !!response.data,
        });
        return response;
      },
      async (error) => {
        if (this.shouldRetry(error)) {
          return this.retryRequest(error);
        }
        return Promise.reject(error);
      }
    );
  }

  private shouldRetry(error: AxiosError): boolean {
    const config = error.config as any;
    return (
      config &&
      config.retry !== undefined &&
      config.retryCount < this.config.retryCount &&
      (error.code === "ECONNABORTED" ||
        (error.response && error.response.status >= 500))
    );
  }

  private async retryRequest(error: AxiosError): Promise<any> {
    const config = error.config as any;
    config.retryCount = (config.retryCount || 0) + 1;

    await new Promise((resolve) => setTimeout(resolve, this.config.retryDelay));

    return this.client(config);
  }

  private handleError(error: unknown): ApiError {
    if (error instanceof AxiosError) {
      const statusCode = error.response?.status;

      if (!error.response) {
        return {
          code: "NETWORK_ERROR",
          message: "网络连接失败，请检查您的网络设置后重试",
          status: 0,
        };
      }

      if (error.code === "ECONNABORTED") {
        return {
          code: "TIMEOUT",
          message: "请求响应超时，请检查网络连接并重试",
          status: 408,
        };
      }

      if (statusCode === 401 || statusCode === 403) {
        return {
          code: "AUTH_ERROR",
          message: "API 认证失败，请检查您的 API Key 设置",
          status: statusCode,
        };
      }

      if (statusCode === 429) {
        return {
          code: "RATE_LIMIT",
          message: "请求过于频繁，请稍等片刻后重试",
          status: 429,
        };
      }

      return {
        code: "API_ERROR",
        message: error.response?.data?.error?.message || "请求失败，请稍后重试",
        status: statusCode,
        details: error.response?.data,
      };
    }

    return {
      code: "UNKNOWN_ERROR",
      message: "发生未知错误，请刷新页面重试",
      details: error,
    };
  }

  async sendMessage(messages: ChatMessage[]): Promise<ChatCompletionResponse> {
    try {
      const requestBody: DeepseekRequest = {
        model: DEFAULT_MODEL,
        messages: this.prepareMessages(messages),
        temperature: 0.7,
        max_tokens: 2000,
        stream: false
      }

      console.log('Request body:', requestBody)
      const { data } = await this.client.post('/chat/completions', requestBody)
      console.log('Raw API response:', data)

      // 检查响应格式
      if (!data.choices?.[0]?.message?.content) {
        console.error('Invalid response format:', data)
        throw new Error('无效的响应格式')
      }

      // 提取消息内容
      const messageContent = data.choices[0].message.content
      console.log('Extracted message content:', messageContent)

      // 返回处理后的响应
      return {
        message: messageContent,
        usage: {
          promptTokens: data.usage?.prompt_tokens || 0,
          completionTokens: data.usage?.completion_tokens || 0,
          totalTokens: data.usage?.total_tokens || 0
        }
      }
    } catch (error) {
      console.error('Send message error:', error)
      const apiError = this.handleError(error)
      return {
        message: '',
        error: apiError.message
      }
    }
  }

  private prepareMessages(messages: ChatMessage[]): DeepseekMessage[] {
    return [
      { role: "system", content: DEFAULT_SYSTEM_MESSAGE },
      ...messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      })),
    ];
  }

  async sendMessageStream(
    messages: ChatMessage[],
    onProgress: (content: string, delta: string) => void
  ): Promise<ChatCompletionResponse> {
    try {
      const requestBody: DeepseekRequest = {
        model: DEFAULT_MODEL,
        messages: this.prepareMessages(messages),
        temperature: 0.7,
        max_tokens: 2000,
        stream: true,
      };

      const response = await this.client.post(
        "/chat/completions",
        requestBody,
        {
          responseType: "stream",
        }
      );

      let fullContent = "";
      let hasReceivedContent = false;

      for await (const chunk of response.data) {
        const lines = chunk.toString().split("\n").filter(Boolean);
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6)) as StreamChunk;
              const deltaContent = data.choices?.[0]?.delta?.content || "";
              if (deltaContent) {
                hasReceivedContent = true;
                fullContent += deltaContent;
                onProgress(fullContent, deltaContent);
              }
            } catch (e) {
              console.error("Error parsing stream chunk:", e);
            }
          }
        }
      }

      if (!hasReceivedContent) {
        throw new Error("未收到任何响应内容");
      }

      return {
        message: fullContent,
      };
    } catch (error) {
      const apiError = this.handleError(error);
      return {
        message: "",
        error: apiError.message,
      };
    }
  }
}

// Export API instance
export const api = new ApiClient();
export const { sendMessage, sendMessageStream } = api;
